import { Maybe, Optional } from '@vimeo/core/types/generic';

import { getAbsoluteNow } from './time';

const SECONDS_IN_MINUTE: number = 60;
const SECONDS_IN_HOUR: number = 3600;
const REGEX_TO_VALIDATE_TIMING: RegExp =
  /^(0[1-9]|1[0-1]):[0-5][0-9]:[0-5]\d$|^[0-5][0-9]:[0-5]\d$/; // validate to match 00:00 and 00:00:00 patterns

export function validateTimingToShow(timingStr: string): boolean {
  return REGEX_TO_VALIDATE_TIMING.test(timingStr);
}

export function getTimeComponents(time: Maybe<number>): Array<string> {
  if (!time || time < 0) {
    return ['00', '00'];
  }

  const timeComponents: Array<string> = [];

  const seconds: number = (time % SECONDS_IN_HOUR) % SECONDS_IN_MINUTE;
  timeComponents.push(seconds < 10 ? `0${seconds}` : `${seconds}`);
  time -= seconds;

  const minutes: number = (time % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE;
  timeComponents.push(minutes < 10 ? `0${minutes}` : `${minutes}`);
  time -= minutes * SECONDS_IN_MINUTE;

  const hours: number = time / SECONDS_IN_HOUR;
  if (hours > 0) {
    timeComponents.push(hours < 10 ? `0${hours}` : `${hours}`);
  }
  return timeComponents.reverse();
}

export function convertTimingFromIntToStr(secondsLeft: Maybe<number>) {
  return getTimeComponents(secondsLeft).join(':');
}

export function calculateStreamingTime(
  startedTime: Optional<number>,
  endedTime?: Optional<number>,
): string {
  if (startedTime === null) {
    return '00:00';
  }

  const startedTimestamp: number = new Date(startedTime * 1000).getTime();
  const endedTimestamp: number = endedTime
    ? new Date(endedTime * 1000).getTime()
    : getAbsoluteNow();
  const elapsedSeconds: number = Math.floor((endedTimestamp - startedTimestamp) / 1000);

  return convertTimingFromIntToStr(elapsedSeconds);
}

/**
 * Safely create date formatter for export instances.
 * Try to create based on provided locale and fallback to system default in case of exception.
 *
 * @example 1700671761000 + en-US -> Nov 22, 06:49 PM
 *
 * @param locale - target locale to format date with
 * @returns formatter to process timestamps in a desired way
 */
export function exportDateFormatter(locale: string): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale || 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return new Intl.DateTimeFormat('default', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
